import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, type RequestResult } from "@touchscreen/api-client";
import { useTouchscreenContext } from "./TouchscreenProvider.js";
import { touchscreenKeys } from "./queryKeys.js";
import { isCoolingDown, markRequested } from "./recently-requested.js";

export type RequestTrackState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; result: RequestResult }
  | { status: "error"; error: ApiError };

export interface RequestTrackOptions {
  /** Required when the venue's geofence is active — validated fresh at submit time, never cached. */
  position?: { lat: number; lng: number };
}

/**
 * Owns the idempotency key and the optimistic local cooldown hint. `duplicate: true` on the
 * result is success — the caller should render the normal confirmation either way. There is
 * nothing for the punter to act on differently.
 */
export function useRequestTrack(): {
  state: RequestTrackState;
  requestTrack: (smId: string, options?: RequestTrackOptions) => Promise<RequestResult | null>;
  reset: () => void;
  isCoolingDown: (smId: string) => boolean;
} {
  const { client, venue } = useTouchscreenContext();
  const queryClient = useQueryClient();

  const cooldownMinutes = venue?.requests.limits.track_cooldown_minutes ?? 60;

  const mutation = useMutation({
    mutationFn: async (vars: { smId: string; options?: RequestTrackOptions }): Promise<RequestResult> => {
      const idempotencyKey = createIdempotencyKey(vars.smId);
      return client.requestTrack(vars.smId, idempotencyKey, vars.options?.position);
    },
    onSuccess: (_result, vars) => {
      markRequested(vars.smId);
      // "Remaining this hour" and the queued list are both stale the moment a request lands.
      if (venue) void queryClient.invalidateQueries({ queryKey: touchscreenKeys.requestsMine(venue.ref) });
    },
  });

  const requestTrack = useCallback(
    async (smId: string, options?: RequestTrackOptions): Promise<RequestResult | null> => {
      try {
        return await mutation.mutateAsync({ smId, options });
      } catch {
        return null;
      }
    },
    [mutation],
  );

  const state: RequestTrackState = mutation.isPending
    ? { status: "loading" }
    : mutation.isError
      ? { status: "error", error: toApiError(mutation.error) }
      : mutation.isSuccess
        ? { status: "success", result: mutation.data }
        : { status: "idle" };

  return {
    state,
    requestTrack,
    reset: mutation.reset,
    isCoolingDown: (smId: string) => isCoolingDown(smId, cooldownMinutes),
  };
}

function createIdempotencyKey(smId: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${smId}:${random}`;
}

function toApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError({ code: "unknown", status: 0, message: String(error) });
}
